import { type Thread, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SidebarItemMenuItems } from '@/components/blocks/Layout/Sidebar/SidebarItemMenuItems';
import { IconButton } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { getArchiveErrorDescription } from '@/features/chat/archive-error';
import { MoveToProjectDropdownSubMenu } from '@/features/projects/move-to-project';
import { confirmChatListDeletion } from '@/features/settings/proactive-agent-chat-deletion';
import { useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { isTouchPrimaryViewport } from '@/utils/touch-primary';

export const ChatRowActions = observer(function ChatRowActions({
  thread,
  title,
  isSelected,
  isOpen,
  onOpenChange,
  onRename,
  onToggleSelect,
}: {
  thread: Thread;
  title: string;
  isSelected: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onRename: () => void;
  onToggleSelect: () => void;
}) {
  const { threads } = useStores();
  const { openTab } = useTabs();
  // Bulk selection already starts from the hover checkbox on mouse-primary
  // devices; the menu entry is the touch discoverability path
  const showSelectItem = isTouchPrimaryViewport();

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        <IconButton
          icon={Icons.moreHorizontal}
          size="default"
          variant="secondary"
          aria-label={`More actions for ${title}`}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 opacity-0',
            'data-[state=open]:opacity-100 data-[state=open]:bg-secondary-hover',
            'touch:opacity-100',
            'group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100',
            'transition-opacity duration-150'
          )}
          onClick={(event) => event.stopPropagation()}
        />
      </DropdownMenu.Trigger>
      {isOpen && (
        <DropdownMenu.Content
          align="end"
          className="min-w-36"
          fit
          onCloseAutoFocus={(event) => event.preventDefault()}
          onClick={(event) => event.stopPropagation()}
        >
          {showSelectItem && (
            <>
              <DropdownMenu.Item
                onClick={onToggleSelect}
                aria-label={`${isSelected ? 'Deselect' : 'Select'} ${title}`}
              >
                <Icons.listChecks />
                {isSelected ? 'Deselect' : 'Select'}
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
            </>
          )}
          <SidebarItemMenuItems
            variant="dropdown"
            title={title}
            isFavorited={thread.starred}
            onOpenInTab={() => openTab({ path: `/chats/${thread.id}` })}
            onRename={onRename}
            onFavorite={() => {
              threads.updateThread(thread, { starred: !thread.starred });
              trackEvent('CHAT_STARRED', {
                chatId: thread.id,
                favorite: !thread.starred,
              });
            }}
            onArchive={async () => {
              try {
                await threads.archiveThread(thread);
                trackEvent('CHAT_ARCHIVED', { chatId: thread.id });
              } catch (error) {
                toast.error('Failed to archive chat', {
                  description: getArchiveErrorDescription(error, 'archive'),
                });
              }
            }}
            onDelete={async () => {
              if (await confirmChatListDeletion([thread.id]))
                await threads.delete(thread);
            }}
            extra={<MoveToProjectDropdownSubMenu threadId={thread.id} />}
          />
        </DropdownMenu.Content>
      )}
    </DropdownMenu.Root>
  );
});
